import { useEffect, useRef, useState } from 'react';
import { ChevronRight, Play, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { images, waterFeatureTypes, whyDifferent } from '../mock/data';

const LOGO_VIDEO_URL = process.env.REACT_APP_LOGO_VIDEO_URL;

export const Home = () => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-in-section');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero Section */}
      <section ref={heroRef} className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={images.hero}
            alt="Natural water landscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/40 via-stone-900/20 to-stone-50"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-light text-white mb-8 tracking-tight leading-tight animate-fade-in-up">
            We shape water<br />from the land itself.
          </h1>
          <p className="text-lg md:text-xl text-stone-200 mb-12 max-w-2xl mx-auto leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Landscape-integrated water design shaped by terrain, climate, and human experience.
          </p>
          <Link
            to="/collaborate"
            className="inline-flex items-center gap-2 bg-white text-stone-900 px-8 py-4 rounded-sm hover:bg-stone-100 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 animate-fade-in-up"
            style={{ animationDelay: '0.4s' }}
          >
            Start a Project
            <ChevronRight size={20} />
          </Link>
        </div>

        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Logo Animation Section */}
      <section className="py-12 px-6 lg:px-12 bg-white fade-in-section">
        <div className="max-w-2xl mx-auto">
          <div className="relative rounded-sm overflow-hidden shadow-lg">
            <video
              className="w-full h-auto"
              autoPlay
              playsInline
            >
              <source src={LOGO_VIDEO_URL} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-24 px-6 lg:px-12 fade-in-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-light text-stone-800 mb-8 tracking-tight">
            Landscape-Integrated Water Design
          </h2>
          <p className="text-lg text-stone-600 leading-relaxed mb-6">
            Pond Skape creates site-responsive water environments that integrate naturally with land, climate, and spatial experience.
          </p>
          <p className="text-lg text-stone-600 leading-relaxed">
            From quiet reflection to flowing natural movement and living environments—every project responds to its surroundings and the feeling it is meant to create. Nothing is standard. Every design is born from context.
          </p>
        </div>
      </section>

      {/* Design Philosophy */}
      <section className="py-24 bg-white fade-in-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative h-[500px] rounded-sm overflow-hidden shadow-xl group">
              <img
                src={images.about}
                alt="Site-responsive water design"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div>
              <h2 className="text-4xl font-light text-stone-800 mb-6 tracking-tight">
                Land Before Form
              </h2>
              <p className="text-stone-600 leading-relaxed mb-6">
                We design water environments that respond to place and purpose. Each project begins with understanding the land—slope, drainage, sunlight, climate, and spatial movement.
              </p>
              <p className="text-stone-600 leading-relaxed mb-8">
                Form follows environment. Flow before control. Experience before decoration. Ecology where appropriate. Design for real conditions.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-stone-800 border-b-2 border-stone-800 pb-1 hover:text-stone-600 hover:border-stone-600 transition-all duration-300"
              >
                Our Design Philosophy
                <ChevronRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Different */}
      <section className="py-24 px-6 lg:px-12 fade-in-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light text-stone-800 mb-16 text-center tracking-tight">
            Why Choose Pond Skape
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyDifferent.map((item, index) => (
              <div
                key={index}
                className="p-8 bg-white rounded-sm shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <h3 className="text-xl font-medium text-stone-800 mb-4">{item.title}</h3>
                <p className="text-stone-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects Gallery */}
      <section className="py-24 bg-stone-900 fade-in-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-4xl md:text-5xl font-light text-white mb-16 text-center tracking-tight">
            Types of Water Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {waterFeatureTypes.slice(0, 3).map((project) => (
              <div
                key={project.id}
                className="group relative h-96 rounded-sm overflow-hidden cursor-pointer"
              >
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-stone-900/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white transform transition-transform duration-300 group-hover:translate-y-0">
                  <h3 className="text-2xl font-light mb-3">{project.title}</h3>
                  <p className="text-sm text-stone-300 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-24 px-6 lg:px-12 bg-white fade-in-section">
        <div className="max-w-5xl mx-auto">
          <div className="relative h-[500px] rounded-sm overflow-hidden shadow-2xl group cursor-pointer" onClick={() => setIsVideoModalOpen(true)}>
            <img
              src={images.projects[0]}
              alt="Design process video"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-stone-900/40 group-hover:bg-stone-900/30 transition-all duration-300"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-xl">
                <Play size={32} className="text-stone-900 ml-1" />
              </div>
            </div>
            <div className="absolute bottom-8 left-8 text-white">
              <p className="text-xl font-light">Watch Our Design Process</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 lg:px-12 bg-stone-800 fade-in-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-light text-white mb-8 tracking-tight">
            Let's Create Together
          </h2>
          <p className="text-lg text-stone-300 mb-12 leading-relaxed">
            Every project begins with understanding place, land, and experience. We'd love to explore how water can shape your environment.
          </p>
          <Link
            to="/collaborate"
            className="inline-flex items-center gap-2 bg-white text-stone-900 px-10 py-5 rounded-sm hover:bg-stone-100 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
          >
            Start a Conversation
            <ChevronRight size={20} />
          </Link>
        </div>
      </section>

      {/* Video Modal */}
      {isVideoModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6 animate-fade-in"
          onClick={() => setIsVideoModalOpen(false)}
        >
          <div className="relative w-full max-w-5xl aspect-video bg-stone-900 rounded-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setIsVideoModalOpen(false)}
              className="absolute top-4 right-4 z-10 text-white hover:text-stone-300 transition-colors duration-300"
            >
              <X size={32} />
            </button>
            <div className="w-full h-full flex items-center justify-center text-white">
              <p className="text-lg">Video player would be embedded here</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
